import { ThunkDispatch } from "core-integration/src/store";
import { updateElementControls } from "core-integration/src/store/elements/actions";
import {
  getElementControls,
  getElementsFromControls,
  getElementTypeFilterOptions,
} from "core-integration/src/store/elements/reducers";
import { updateElementControlsAndMaxCost } from "core-integration/src/store/elements/thunks";
import { IElementControls } from "core-integration/src/store/elements/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { getToSpend } from "core-integration/src/store/squad/reducers";
import { getTeams } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import range from "lodash/range";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getStatDetails, getStatNames } from "../../../utils/stats";
import {
  ChipRow,
  ChipRowWrapper,
  ChipSelect,
  ChipWrap,
  OffscreenLabelMeasure,
  ReloadIcon,
  ResetChip,
} from "./styles";
import type { DynamicChipSelectProps, FilterChipRowProps } from "./types";

const DynamicChipSelect = ({
  children,
  id,
  label,
  onChange,
  value,
}: DynamicChipSelectProps) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const labelWidthMeasureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const select = selectRef.current;
    const labelWidthMeasure = labelWidthMeasureRef.current;
    if (!select || !labelWidthMeasure) return;

    const selectedOption = select.options[select.selectedIndex];
    if (selectedOption) {
      labelWidthMeasure.textContent = selectedOption.text;
      const textWidth = labelWidthMeasure.offsetWidth;
      const maxWidth = 200;
      select.style.width = `${Math.min(textWidth + 44, maxWidth)}px`;
    }
  }, [value]);

  return (
    <ChipWrap>
      <OffscreenLabelMeasure ref={labelWidthMeasureRef} aria-hidden="true" />
      <ChipSelect
        ref={selectRef}
        id={id}
        aria-label={label}
        onChange={onChange}
        value={value}
      >
        {children}
      </ChipSelect>
    </ChipWrap>
  );
};

export const defaultElementFilters: Pick<IElementControls, "sort" | "filter"> =
  {
    filter: "all",
    sort: "total_points",
  };

const affordableOptionValue = "affordable";

const FilterChipRow = ({ initialSort }: FilterChipRowProps) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { t } = useTranslation();

  const controls = useSelector(getElementControls);
  const elements = useSelector(getElementsFromControls);
  const elementTypeFilterOptions = useSelector(getElementTypeFilterOptions);
  const player = useSelector(getPlayerData);
  const teams = useSelector(getTeams);
  const toSpend = useSelector(getToSpend);

  const defaultSort = initialSort ?? defaultElementFilters.sort;

  const maxCostSelectValue =
    player && controls.maxCost === toSpend
      ? affordableOptionValue
      : controls.maxCost;

  const isModified =
    controls.filter !== defaultElementFilters.filter ||
    controls.sort !== defaultSort ||
    controls.maxCost !== elements.maxCost ||
    !!controls.search;

  const updateControls = (c: IElementControls) =>
    dispatch(updateElementControls(c));
  const updateControlsAndMaxCost = (c: IElementControls) =>
    dispatch(updateElementControlsAndMaxCost(c));

  const handleReset = () =>
    updateControlsAndMaxCost({
      ...controls,
      ...defaultElementFilters,
      sort: defaultSort,
      search: "",
    });

  const filterTeams = teams
    .filter((team: any) => team.unavailable === false)
    .sort((a: any, b: any) => a.name.localeCompare(b.name));

  const sortOptions = getStatNames(t).map((s) => ({
    label: getStatDetails(s, t)!.label,
    value: s,
  }));

  const handleMaxCostChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const maxCost =
      e.target.value === affordableOptionValue
        ? toSpend
        : Number(e.target.value);

    updateControls({ ...controls, maxCost });
  };

  return (
    <ChipRowWrapper>
      <ChipRow>
        <DynamicChipSelect
          id="filter"
          value={controls.filter}
          onChange={(e) =>
            updateControlsAndMaxCost({ ...controls, filter: e.target.value })
          }
          label={t("elementFilter.view", "View")}
        >
          <option value="view-by" disabled hidden>
            {t("elementFilter.viewBy", "View by")}
          </option>
          <optgroup label={t<string>("elementFilter.global", "Global")}>
            <option value="all" aria-selected={controls.filter === "all"}>
              {t("elementFilter.allPlayers", "All players")}
            </option>
            {player && player.entry && (
              <option value="wl" aria-selected={controls.filter === "wl"}>
                {t("elementFilter.watchList", "Watchlist")}
              </option>
            )}
          </optgroup>
          <optgroup
            label={t<string>("elementFilter.byPosition", "By position")}
          >
            {elementTypeFilterOptions.map((o) => (
              <option
                key={o.value}
                value={o.value}
                aria-selected={controls.filter === o.value}
              >
                {o.label}
              </option>
            ))}
          </optgroup>
          <optgroup label={t<string>("elementFilter.topLeague", "Team")}>
            {filterTeams.map((o: any) => (
              <option
                key={`te_${o.id}`}
                value={`te_${o.id}`}
                aria-selected={controls.filter === `te_${o.id}`}
              >
                {o.name}
              </option>
            ))}
          </optgroup>
        </DynamicChipSelect>

        <DynamicChipSelect
          id="sort"
          value={controls.sort}
          onChange={(e) =>
            updateControls({ ...controls, sort: e.target.value })
          }
          label={t("sortedBy", "Sorted by")}
        >
          {sortOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </DynamicChipSelect>

        <DynamicChipSelect
          id="maxCost"
          value={maxCostSelectValue}
          onChange={handleMaxCostChange}
          label={t("elementList.maxCost.label", "Max cost")}
        >
          {player && (
            <option key="affordable" value={affordableOptionValue}>
              {t("elementList.affordable", "Affordable")} (&euro;
              {integerToMoney(toSpend, 10)})
            </option>
          )}
          {range(elements.maxCost, elements.minCost - 1, -5).map((cost) => (
            <option key={cost} value={cost}>
              &euro;{integerToMoney(cost, 10)}
            </option>
          ))}
        </DynamicChipSelect>
        <ResetChip type="button" onClick={handleReset} disabled={!isModified}>
          {t("elementList.reset", "Reset")}
          <ReloadIcon aria-hidden="true" width={14} height={14} />
        </ResetChip>
      </ChipRow>
    </ChipRowWrapper>
  );
};

export default FilterChipRow;
